"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { clearColorCache } from "./colors";

const preventDarkMode = false;

type ColorMode = "system" | "light" | "dark";
type ResolvedTheme = "light" | "dark";

interface ColorModeContextType {
  mode: ColorMode;
  colorMode: ResolvedTheme;
  isDarkTheme: boolean;
  toggleTheme: () => void;
}

const ColorModeContext = createContext<ColorModeContextType>({
  mode: "system",
  colorMode: "light",
  isDarkTheme: false,
  toggleTheme: () => {
    /* Default implementation */
  },
});

export const useColorMode = () => useContext(ColorModeContext);

interface ColorModeProviderProps {
  children: React.ReactNode;
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getSavedMode(): ColorMode {
  if (typeof window === "undefined") return "system";
  const saved = localStorage.getItem("colorModePreference");
  if (saved === "light" || saved === "dark" || saved === "system") {
    return saved;
  }
  return "system";
}

function resolveTheme(mode: ColorMode): ResolvedTheme {
  if (mode === "system") {
    return getSystemTheme();
  }
  return mode;
}

export const ColorModeProvider: React.FC<ColorModeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ColorMode>(getSavedMode);
  const [colorMode, setColorMode] = useState<ResolvedTheme>(() => resolveTheme(getSavedMode()));
  const isDarkTheme = colorMode === "dark";

  const applyTheme = useCallback((theme: ResolvedTheme) => {
    if (theme === "dark") {
      document.documentElement.classList.add("theme-dark");
      document.body.classList.add("theme-dark");
    } else {
      document.documentElement.classList.remove("theme-dark");
      document.body.classList.remove("theme-dark");
    }
    clearColorCache();
  }, []);

  useEffect(() => {
    clearColorCache();

    const theme = resolveTheme(mode);
    setColorMode(theme);
    applyTheme(theme);

    if (mode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      const handleSystemThemeChange = (e: MediaQueryListEvent) => {
        const newTheme = e.matches ? "dark" : "light";
        setColorMode(newTheme);
        applyTheme(newTheme);
      };

      mediaQuery.addEventListener("change", handleSystemThemeChange);

      return () => {
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
      };
    }
  }, [mode, applyTheme]);

  const toggleTheme = useCallback(() => {
    setMode((currentMode) => {
      const nextMode: ColorMode = currentMode === "system" ? "light" : currentMode === "light" ? "dark" : "system";

      localStorage.setItem("colorModePreference", nextMode);

      return nextMode;
    });
  }, []);

  if (preventDarkMode) {
    return (
      <ColorModeContext.Provider
        value={{
          mode: "light",
          colorMode: "light",
          isDarkTheme: false,
          toggleTheme: () => {
            /* Default implementation */
          },
        }}
      >
        {children}
      </ColorModeContext.Provider>
    );
  }

  return (
    <ColorModeContext.Provider value={{ mode, colorMode, isDarkTheme, toggleTheme }}>
      {children}
    </ColorModeContext.Provider>
  );
};
